<script lang="ts">
	import { onMount, type Snippet } from 'svelte';
	import type { DropCardVariant } from '../../constants/dropCardVariants';
	import { getDropAbVariant } from '../../utils/getDropAbVariant/getDropAbVariant';
	import { Anchor } from '../DropAnchor/Anchor';
	import { getAnalytics, track } from '$global/utils/track';

	interface Props {
		// The grid's layouts, from getDropCardVariants: never empty.
		variants: DropCardVariant[];
		// The grid's Directus id, so the exposure names the section tested.
		sectionId: string;
		hasTabs: boolean;
		// Renders the grid in one layout. `withAnchor` is false while the
		// layouts are A/B tested: the `#cards` anchor is then rendered once
		// here, outside the arms, as an anchor inside a hidden arm can neither
		// be scrolled to nor share its id with the other arms'.
		children: Snippet<[DropCardVariant, boolean]>;
	}

	let { variants, sectionId, hasTabs, children }: Props = $props();

	let isAbTest = $derived(variants.length > 1);

	onMount(() => {
		if (!isAbTest) {
			return;
		}

		const variant = getDropAbVariant(variants);

		// A global property, so every later event of the visit (add to cart,
		// checkout) carries the layout the visitor was shown.
		getAnalytics()?.setGlobalProperties({ drop_card_variant: variant });
		track('drop_card_variant_viewed', {
			variant,
			variants: variants.join(','),
			section_id: sectionId,
		});
	});
</script>

{#if isAbTest}
	<Anchor {hasTabs} id="#cards" />
	<!-- Every arm is rendered, as the page is cached for all visitors alike;
	the rules in app.css hide all but the visitor's arm before the first
	paint, keyed on the arm the head script wrote on the root element. -->
	<!-- The group lets a forced variant (`?heni_ab=grid`, outside production)
	win over the visitor's arm when the grid offers it: see app.css. -->
	<div class="contents" data-ab-group>
		{#each variants as variant, arm (variant)}
			<div
				class="contents"
				data-ab-arms={variants.length}
				data-ab-arm={arm}
				data-ab-name={variant}
				data-cy={`drop-card-variant-${variant}`}
			>
				{@render children(variant, false)}
			</div>
		{/each}
	</div>
{:else}
	{@render children(variants[0], true)}
{/if}
